import { FC, useState, useMemo, useRef, useEffect } from "react";
import { Card, Table, Pagination, Modal, Form, Input, Button, notification, Select, Tabs } from "antd";
import { SettingOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import PageLayout from '../components/PageLayout';
import { fullname, role as roleKey, email as emailKey } from '../utils/data';
import { 
  useGetFaqsQuery,
  useLazyGetFaqsQuery,
  useAddFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
  useGetChatbotSettingsQuery,
  useUpdateChatbotSettingsMutation
} from '../redux/api/chatbotApi';

interface FAQ {
  id: string | number;
  categoryId: string | number;
  question: string;
  answer: string;
  legacy_source?: string;
  categoryName?: string;
  chatbot_type?: string;
}

const ChatbotSettings: FC = () => {
  const navigate = useNavigate();
  const [notifApi, notifContextHolder] = notification.useNotification();
  
  // Get current user info from localStorage
  const currentUser = {
    role: localStorage.getItem(roleKey) || 'user',
    fullname: localStorage.getItem(fullname) || 'User',
    email: localStorage.getItem(emailKey) || ''
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedChatbotType, setSelectedChatbotType] = useState<string>("all");
  const [categoryNames, setCategoryNames] = useState<string[]>([]);
  const [showCustomCategory, setShowCustomCategory] = useState(false);

  const queryParams: any = {};
  if (searchQuery) queryParams.search = searchQuery;
  if (selectedChatbotType && selectedChatbotType !== "all") queryParams.chatbot_type = selectedChatbotType;
  if (selectedCategory && selectedCategory !== "all") queryParams.legacy_source = selectedCategory;

  const { data: faqsData, isLoading: loading } = useGetFaqsQuery(queryParams);
  const { data: filterData } = useGetFaqsQuery({ 
    chatbot_type: selectedChatbotType !== "all" ? selectedChatbotType : undefined 
  });

  const [addFaq] = useAddFaqMutation();
  const [updateFaq] = useUpdateFaqMutation();
  const [deleteFaq] = useDeleteFaqMutation();
  const [triggerGetModalCategories] = useLazyGetFaqsQuery();

  const faqs = useMemo(() => {
    if (!faqsData) return [];
    const flattened: FAQ[] = [];
    faqsData.forEach((category: any) => {
      if (selectedCategory && selectedCategory !== "all" && category.name !== selectedCategory) {
        return;
      }
      if (category.faqs && Array.isArray(category.faqs)) {
        category.faqs.forEach((faq: any) => {
          flattened.push({
            id: faq.id,
            categoryId: category.id,
            question: faq.question,
            answer: faq.answer,
            legacy_source: faq.legacy_source,
            categoryName: category.name,
            chatbot_type: category.chatbot_type
          });
        });
      }
    });
    return flattened;
  }, [faqsData, selectedCategory]);

  useEffect(() => {
    if (filterData) {
      const categoriesSet = new Set<string>();
      
      filterData.forEach((category: any) => {
        if (category.name) categoriesSet.add(category.name);
      });
      
      setCategoryNames(Array.from(categoriesSet));
    }
  }, [filterData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [form] = Form.useForm();
  const [settingsForm] = Form.useForm();
  const [modalCategoryNames, setModalCategoryNames] = useState<string[]>([]);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deletingFaq, setDeletingFaq] = useState<FAQ | null>(null);

  const { data: chatbotSettings, isLoading: isLoadingSettings } = useGetChatbotSettingsQuery();
  const [updateChatbotSettings] = useUpdateChatbotSettingsMutation();

  const settingsData = chatbotSettings ? (Array.isArray(chatbotSettings) ? chatbotSettings[0] : chatbotSettings) : null;
  const botName = settingsData?.name || 'Chatbot';

  useEffect(() => {
    if (isLoadingSettings) return;

    if (chatbotSettings) {
      // In case the API returns an array, pick the first object. If it's just an object, use it directly.
      const data = Array.isArray(chatbotSettings) ? chatbotSettings[0] : chatbotSettings;
      
      if (data) {
        settingsForm.setFieldsValue({
          name: data.name || '',
          welcome_notification: data.welcome_notification || 'Hi I’m Clara! How can I help you today?',
          default_opening_message: data.default_opening_message || 'Hello! I\'m Clara. I\'m here to answer questions...',
          trigger_keywords: data.trigger_keywords || [],
          suggested_questions: data.suggested_questions || []
        });
      }
    }
  }, [chatbotSettings, isLoadingSettings, settingsForm]);

  const fetchModalCategories = async (type: string) => {
    try {
      const res = await triggerGetModalCategories({ chatbot_type: type !== "all" ? type : undefined }).unwrap();
      const cats = new Set<string>();
      res.forEach((c: any) => {
        if (c.name) cats.add(c.name);
      });
      setModalCategoryNames(Array.from(cats));
    } catch (e) {
      console.error(e);
    }
  };

  const handleModalChatbotTypeChange = (value: string) => {
    fetchModalCategories(value);
    form.setFieldsValue({ categoryName: undefined });
    setShowCustomCategory(false);
  };

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [scrollInfo, setScrollInfo] = useState({ left: 0, width: 0, client: 0 });

  const pagedData = useMemo(() => faqs.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  ), [faqs, currentPage, pageSize]);

  // Custom Scrollbar Sync Logic
  useEffect(() => {
    const tableBody = tableContainerRef.current?.querySelector('.ant-table-content');
    if (!tableBody) return;

    const onScroll = () => {
      setScrollInfo({
        left: tableBody.scrollLeft,
        width: tableBody.scrollWidth,
        client: tableBody.clientWidth
      });
    };

    tableBody.addEventListener('scroll', onScroll);
    onScroll(); // Initial sync

    window.addEventListener('resize', onScroll);
    
    return () => {
      tableBody.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [faqs, pagedData]);

  const handleDelete = (faq: FAQ) => {
    setDeletingFaq(faq);
    setDeleteConfirmVisible(true);
  };

  const confirmDelete = async () => {
    if (!deletingFaq) return;
    try {
      await deleteFaq(deletingFaq.id).unwrap();
      notifApi.success({ message: 'FAQ deleted successfully', placement: 'topRight' });
    } catch (error: any) {
      notifApi.error({
        message: 'Error',
        description: error.data?.detail || 'Failed to delete FAQ',
        placement: 'topRight'
      });
    } finally {
      setDeleteConfirmVisible(false);
      setDeletingFaq(null);
    }
  };

  const handleEdit = (faq: FAQ) => {
    setEditingFaq(faq);
    
    let isCustom = false;
    let initialValues: any = { ...faq };
    
    const initialType = faq.chatbot_type || 'technology';
    fetchModalCategories(initialType);
    
    if (faq.categoryName && !categoryNames.includes(faq.categoryName)) {
      isCustom = true;
      initialValues.categoryName = 'New Category';
      initialValues.custom_category_name = faq.categoryName;
    }
    
    setShowCustomCategory(isCustom);
    form.setFieldsValue(initialValues);
    setIsModalVisible(true);
  };

  const handleAdd = () => {
    setEditingFaq(null);
    setShowCustomCategory(false);
    form.resetFields();
    fetchModalCategories('technology'); // Default initial value
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    form.validateFields().then(async (values) => {
      try {
        let finalCategoryName = values.categoryName || '';
        if (finalCategoryName === 'New Category') {
          finalCategoryName = values.custom_category_name || '';
        }

        if (editingFaq) {
          // Edit mode
          const payload = {
            name: finalCategoryName,
            chatbot_type: values.chatbot_type || 'technology',
            faqs: [
              {
                id: editingFaq.id,
                question: values.question,
                answer: values.answer
              }
            ]
          };
          
          await updateFaq({ id: editingFaq.id, data: payload }).unwrap();
          notifApi.success({ message: 'FAQ updated successfully', placement: 'topRight' });
          setIsModalVisible(false);
        } else {
          // Add mode
          const payload = {
            name: finalCategoryName,
            chatbot_type: values.chatbot_type || 'technology',
            faqs: [
              {
                question: values.question,
                answer: values.answer,
                legacy_source: finalCategoryName
              }
            ]
          };
          
          await addFaq(payload).unwrap();
          notifApi.success({ message: 'FAQ added successfully', placement: 'topRight' });
          setIsModalVisible(false);
        }
      } catch (error: any) {
        notifApi.error({
          message: 'Error',
          description: error.data?.detail || 'Failed to save FAQ',
          placement: 'topRight'
        });
      }
    }).catch(() => {
      // Validation failed, do nothing
    });
  };

  const columns = [
    {
      title: 'Question',
      dataIndex: 'question',
      key: 'question',
      width: '35%',
      render: (text: string) => <span className="font-medium text-[#1F2A37]">{text}</span>
    },
    {
      title: 'Answer',
      dataIndex: 'answer',
      key: 'answer',
      width: '40%',
      render: (text: string) => <span className="text-[#64748b]">{text}</span>
    },
    {
      title: 'Category',
      dataIndex: 'categoryName',
      key: 'categoryName',
      width: '15%',
      render: (text: string, record: FAQ) => (
        <div className="flex flex-col gap-1">
          <span className="text-[#1F2A37]">{text || 'N/A'}</span>
          {record.chatbot_type && (
            <span className="text-[11px] text-[#4682B4] capitalize">{record.chatbot_type}</span>
          )}
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_: any, record: FAQ) => (
        <div className="flex items-center gap-2">
          <Button
            type="text"
            className="text-[#4682B4] hover:text-[#315f85] hover:bg-[#4682B410]"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </div>
      )
    }
  ];

  return (
    <PageLayout>
      {notifContextHolder}
      <AppHeader currentUser={currentUser} />

      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-6 md:px-12 mt-12 md:mt-12 pb-12">
        <Card
          className="premium-gradient-card white mb-6 overflow-hidden rounded-[17px] shadow-sm bg-white"
          bodyStyle={{ padding: '0px' }}
        >
          <style dangerouslySetInnerHTML={{__html: `
            .custom-tabs-styling .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
              color: #4682B4 !important;
            }
            .custom-tabs-styling .ant-tabs-ink-bar {
              background: #4682B4 !important;
            }
            .custom-tabs-styling .ant-tabs-tab:hover {
              color: #4682B4 !important;
            }
          `}} />
          <Tabs
            defaultActiveKey="1"
            className="chatbot-settings-tabs custom-tabs-styling"
            tabBarStyle={{ padding: '16px 24px 0 24px', borderBottom: '1px solid #f0f0f0', marginBottom: 0 }}
            items={[
              {
                key: '1',
                label: <span className="font-semibold px-4 text-base">Clara Settings</span>,
                children: (
                  <div className="p-6 md:p-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-[#4682B4]/10 p-3 rounded-xl flex items-center justify-center">
              <SettingOutlined className="text-[#4682B4] text-[24px]" />
            </div>
            <div>
              <h1 className="!m-0 font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px] md:text-[20px] leading-tight">
                {botName} Settings
              </h1>
              <p className="text-[#64748b] font-['DM_Sans',sans-serif] text-[12px] md:text-[14px] mt-1 font-normal">
                Configure {botName}'s preferences and FAQs
              </p>
            </div>
          </div>

          <Form layout="vertical" form={settingsForm} onFinish={async (values) => {
            try {
              await updateChatbotSettings(values).unwrap();
              notifApi.success({ message: 'Preferences Saved', description: 'Clara settings have been successfully updated.', placement: 'topRight' });
            } catch (error: any) {
              notifApi.error({ message: 'Error Saving Settings', description: error.data?.detail || 'Failed to update preferences.', placement: 'topRight' });
            }
          }}>
            <div className="mb-6">
              <Form.Item label={<span className="font-semibold text-slate-700">Chatbot Name</span>} name="name" rules={[{ required: true }]} className="max-w-md">
                <Input className="h-11 rounded-lg" />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Form.Item label={<span className="font-semibold text-slate-700">Welcome Notification (Auto-Greet)</span>} name="welcome_notification" rules={[{ required: true }]} >
                <Input.TextArea rows={2} className="rounded-lg resize-none" placeholder="e.g. Hi I’m Clara! How can I help you today?" />
              </Form.Item>
              <Form.Item label={<span className="font-semibold text-slate-700">Default Opening Message</span>} name="default_opening_message" rules={[{ required: true }]} >
                <Input.TextArea rows={2} className="rounded-lg resize-none" placeholder="e.g. Hello! I'm Clara. I'm here to answer questions..." />
              </Form.Item>
            </div>

            <div className="mb-6">
              <Form.Item label={<span className="font-semibold text-slate-700">Trigger Keywords</span>} name="trigger_keywords" tooltip="Type a word and press Enter. If a user's question contains any of these, Clara will intercept and offer the Report Wizard.">
                <Select mode="tags" style={{ width: '100%' }} placeholder="Add trigger keywords (e.g., harassment, fired)" className="custom-select" />
              </Form.Item>
            </div>

            <div className="mb-4">
              <h3 className="font-semibold text-slate-700 mb-1">Suggested Questions (Popular Questions)</h3>
              <p className="text-sm text-slate-500">Configure the predefined questions presented to users when they first open the chat.</p>
            </div>

            <Form.List name="suggested_questions">
              {(fields, { add, remove }) => (
                <>
                  <div className="grid grid-cols-1 gap-4">
                    {fields.map(({ key, name, ...restField }) => (
                      <div key={key} className="relative bg-[#F8FAFC] p-4 rounded-xl border border-slate-200">
                        <div className="flex justify-between items-start mb-2">
                          <Form.Item {...restField} name={[name, 'id']} hidden>
                            <Input />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, 'chatbot_type']} rules={[{ required: true, message: 'Missing type' }]} className="mb-0 w-1/3">
                            <Select className="custom-select" placeholder="Select Target Chatbot">
                              <Select.Option value="technology">Technology</Select.Option>
                              <Select.Option value="subscriber">Subscriber</Select.Option>
                              <Select.Option value="both">Both</Select.Option>
                            </Select>
                          </Form.Item>
                          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} className="ml-2" />
                        </div>
                        <Form.Item {...restField} name={[name, 'prompt']} rules={[{ required: true, message: 'Missing prompt' }]} label="Button Label (Prompt)" className="mb-3 mt-3">
                          <Input className="rounded-lg" placeholder="e.g. What is EmployProTech™?" />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'question']} rules={[{ required: true, message: 'Missing question' }]} label="Actual Question (Sent when clicked)" className="mb-3">
                          <Input className="rounded-lg" placeholder="e.g. What is EmployProTech™?" />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'answer']} rules={[{ required: true, message: 'Missing answer' }]} label="Predefined Answer" className="mb-0">
                          <Input.TextArea rows={2} className="rounded-lg resize-none" placeholder="Answer provided to the user" />
                        </Form.Item>
                      </div>
                    ))}
                  </div>
                  <Button type="dashed" onClick={() => add({ chatbot_type: 'both', prompt: '', question: '', answer: '' })} block icon={<PlusOutlined />} className="h-11 mt-4 rounded-lg border-slate-300 text-slate-600 hover:text-[#4682B4] hover:border-[#4682B4]">
                    Add Suggested Question
                  </Button>
                </>
              )}
            </Form.List>

            <div className="flex justify-end mt-6">
              <Button 
                type="primary" 
                htmlType="submit"
                className="bg-[#4682B4] rounded-full px-8 h-11 font-medium shadow-sm hover:shadow-md transition-shadow" 
              >
                Save Preferences
              </Button>
            </div>
          </Form>
                  </div>
                ),
              },
              {
                key: '2',
                label: <span className="font-semibold px-4 text-base">FAQ Management</span>,
                children: (
                  <div className="p-6 md:p-10">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="bg-[#4682B4]/10 p-3 rounded-xl">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#4682B4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13" stroke="#4682B4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 17H12.01" stroke="#4682B4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h2 className="!m-0 font-['Lexend',sans-serif] font-semibold text-[#1f2a37] text-[14px] md:text-[20px] leading-tight">
                  FAQ Management
                </h2>
                <p className="text-[#64748b] font-['DM_Sans',sans-serif] text-[12px] md:text-[14px] mt-1 font-normal">
                  Manage the topics and questions {botName} can answer
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 w-full md:w-auto mt-4 md:mt-0">
              <Input.Search
                placeholder="Search FAQs..."
                allowClear
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onSearch={(value) => setSearchQuery(value)}
                style={{ width: 200 }}
                className="custom-search"
              />
              <Select
                value={selectedChatbotType}
                onChange={value => {
                  setSelectedChatbotType(value);
                  setSelectedCategory("all");
                }}
                style={{ width: 140 }}
                className="custom-select"
              >
                <Select.Option value="all">All Types</Select.Option>
                <Select.Option value="technology">Technology</Select.Option>
                <Select.Option value="subscriber">Subscriber</Select.Option>
              </Select>
              <Select
                value={selectedCategory}
                onChange={value => setSelectedCategory(value)}
                style={{ width: 160 }}
                className="custom-select"
              >
                <Select.Option value="all">All Categories</Select.Option>
                {categoryNames.map(cat => (
                  <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                ))}
              </Select>
              <Button 
                type="primary"
                className="bg-[#4682B4] rounded-full px-6 flex items-center gap-2 h-11 font-medium ml-2"
                onClick={handleAdd}
                icon={<PlusOutlined />}
              >
                Add FAQ
              </Button>
            </div>
          </div>
          
          <div ref={tableContainerRef}>
            <Table
              columns={columns}
              dataSource={pagedData}
              rowKey="id"
              loading={loading}
              className="modern-table bg-[#F8FAFC]"
              pagination={false}
              scroll={{ x: 800 }}
            />
          </div>
          
          {/* Custom Synchronized Scrollbar */}
          {scrollInfo.width > scrollInfo.client && (
            <div className="w-full flex justify-center mt-[19px]">
              <div 
                className="relative h-[4px] rounded-[3px] overflow-hidden" 
                style={{ width: '85.90%', backgroundColor: '#4682B430' }}
              >
                <div 
                  className="absolute h-full bg-[#4682B4] rounded-[3px] transition-all duration-100"
                  style={{
                    width: `${(scrollInfo.client / scrollInfo.width) * 60}%`,
                    left: `${(scrollInfo.left / (scrollInfo.width - scrollInfo.client || 1)) * (100 - (scrollInfo.client / scrollInfo.width * 60))}%`
                  }}
                />
              </div>
            </div>
          )}

          {/* Separate Pagination */}
          <div className="w-full flex justify-end mt-[22px] md:mt-[32px]">
            <style dangerouslySetInnerHTML={{__html: `
              .custom-pagination-container {
                display: flex !important;
                align-items: center !important;
                justify-content: flex-end !important;
                width: 100% !important;
              }
              .custom-pagination-container .ant-pagination-total-text {
                order: 1 !important;
                color: #555D67 !important;
                font-family: 'Lexend', sans-serif !important;
                font-size: 10px !important;
                font-weight: 300 !important;
                margin-right: 8px !important;
              }
              @media (min-width: 768px) {
                .custom-pagination-container .ant-pagination-total-text {
                  font-size: 14px !important;
                  margin-right: 12px !important;
                }
              }
              /* Arrows Styling */
              .custom-pagination-container .ant-pagination-prev,
              .custom-pagination-container .ant-pagination-next {
                order: 2 !important;
                border: none !important;
                background: transparent !important;
                min-width: 20px !important;
                height: 32px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                color: #1F2A37 !important;
                font-size: 16px !important;
                margin: 0 !important;
              }
              /* Page Item & Jump (Dots) Styling */
              .custom-pagination-container .ant-pagination-item,
              .custom-pagination-container .ant-pagination-jump-prev,
              .custom-pagination-container .ant-pagination-jump-next {
                order: 2 !important;
                border-radius: 4px !important;
                height: 28px !important;
                min-width: 28px !important;
                line-height: 26px !important;
                background: white !important;
                font-family: 'Lexend', sans-serif !important;
                margin: 0 4px !important;
                font-size: 14px !important;
                display: inline-flex !important;
                align-items: center !important;
                justify-content: center !important;
              }
              .custom-pagination-container .ant-pagination-item {
                border: 0.8px solid #4682B4 !important;
              }
              .custom-pagination-container .ant-pagination-jump-prev,
              .custom-pagination-container .ant-pagination-jump-next {
                border: none !important;
              }
              /* Hide non-active items on small screens */
              @media (max-width: 767px) {
                .custom-pagination-container .ant-pagination-item:not(.ant-pagination-item-active),
                .custom-pagination-container .ant-pagination-jump-prev,
                .custom-pagination-container .ant-pagination-jump-next {
                  display: none !important;
                }
              }
              .custom-pagination-container .ant-pagination-item-active {
                border-color: #4682B4 !important;
              }
              .custom-pagination-container .ant-pagination-item-active a {
                color: #1F2A37 !important;
                font-weight: 500 !important;
              }
              /* Options/Page Changer Styling */
              .custom-pagination-container .ant-pagination-options {
                order: 3 !important;
                margin-left: 8px !important;
                display: block !important;
              }
              .custom-pagination-container .ant-select-selector {
                background-color: #032B440D !important;
                border: none !important;
                border-radius: 4px !important;
                height: 28px !important;
                font-family: 'Lexend', sans-serif !important;
                font-size: 12px !important;
                font-weight: 300 !important;
                color: #1F2A37 !important;
                padding: 0 10px !important;
                display: flex !important;
                align-items: center !important;
              }
              .custom-pagination-container .ant-select-selection-item {
                line-height: 28px !important;
                color: #1F2A37 !important;
              }
              .custom-pagination-container .ant-select-arrow {
                color: #1F2A37 !important;
              }
            `}} />
            <Pagination
              className="custom-pagination-container"
              current={currentPage}
              pageSize={pageSize}
              total={faqs.length}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              showSizeChanger
              showTotal={(total) => `Total ${total} FAQs`}
              itemRender={(page, type, originalElement) => {
                if (type === 'prev') return <span className="cursor-pointer font-bold px-1">{"<"}</span>;
                if (type === 'next') return <span className="cursor-pointer font-bold px-1">{">"}</span>;
                return originalElement;
              }}
            />
          </div>
                  </div>
                ),
              },
            ]}
          />
        </Card>
      </div>

      <Modal
        title={<span className="font-['Lexend'] font-medium">{editingFaq ? 'Edit FAQ' : 'Add FAQ'}</span>}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleModalOk}
        okText={editingFaq ? 'Save Changes' : 'Add FAQ'}
        cancelText="Cancel"
        okButtonProps={{ className: 'bg-[#4682B4] hover:bg-[#315f85] border-none rounded-full px-6' }}
        cancelButtonProps={{ className: 'rounded-full' }}
        centered
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-6 max-h-[60vh] overflow-y-auto px-1 custom-scrollbar"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <Form.Item
              name="chatbot_type"
              label={<span className="font-medium text-[#1f2a37]">Chatbot Type</span>}
              rules={[{ required: true, message: 'Please select the chatbot type' }]}
              initialValue="technology"
            >
              <Select className="custom-select h-10" onChange={handleModalChatbotTypeChange}>
                <Select.Option value="technology">Technology</Select.Option>
                <Select.Option value="subscriber">Subscriber</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="categoryName"
              label={<span className="font-medium text-[#1f2a37]">Category Name</span>}
              rules={[{ required: true, message: 'Please select the category name' }]}
            >
              <Select 
                className="custom-select h-10" 
                placeholder="Select category"
                onChange={(value) => setShowCustomCategory(value === 'New Category')}
              >
                {modalCategoryNames.map(cat => (
                  <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                ))}
                <Select.Option value="New Category">+ Add New Category</Select.Option>
              </Select>
            </Form.Item>
          </div>

          {showCustomCategory && (
            <Form.Item
              name="custom_category_name"
              label={<span className="font-medium text-[#1f2a37]">New Category Name</span>}
              rules={[{ required: true, message: 'Please enter a custom category name' }]}
            >
              <Input 
                placeholder="e.g., General Information Form" 
                className="rounded-lg py-2"
              />
            </Form.Item>
          )}

          <Form.Item
            name="question"
            label={<span className="font-medium text-[#1f2a37]">Question</span>}
            rules={[{ required: true, message: 'Please enter the question' }]}
          >
            <Input 
              placeholder="e.g., What is your refund policy?" 
              className="rounded-lg py-2"
            />
          </Form.Item>
          
          <Form.Item
            name="answer"
            label={<span className="font-medium text-[#1f2a37]">Answer</span>}
            rules={[{ required: true, message: 'Please enter the answer' }]}
          >
            <Input.TextArea 
              placeholder="Provide a clear and concise answer..."
              className="rounded-lg py-2"
              autoSize={{ minRows: 4, maxRows: 8 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<span className="font-['Lexend'] font-medium">Delete FAQ</span>}
        open={deleteConfirmVisible}
        onCancel={() => {
          setDeleteConfirmVisible(false);
          setDeletingFaq(null);
        }}
        onOk={confirmDelete}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true, className: 'rounded-full px-6' }}
        cancelButtonProps={{ className: 'rounded-full' }}
        centered
      >
        <p className="text-[#64748b] mt-4">Are you sure you want to delete this FAQ? This action cannot be undone.</p>
      </Modal>
    </PageLayout>
  );
};

export default ChatbotSettings;
